'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import HeroSection from '@/components/marketing/HeroSection';

type Locale = 'fr' | 'en' | 'es';

// ─── CONTENT ─────────────────────────────────────────────────────────────────
const C = {
  fr: {
    hero: {
      badge: 'Programme de fidélité · Sans carte · Sans application',
      title: 'La fidélité client,\nsans carte\net sans application.',
      subtitle: 'Le vendeur garde le contrôle total de la vente. Le client saisit son numéro sur le terminal, consulte ses points — et c\'est tout.',
      cta: 'Essayer gratuitement pendant 30 jours',
      ctaNote: '19 € HT/mois ou 190 € HT/an · Aucune carte bancaire',
      ctaSecondary: 'Voir comment ça fonctionne',
      trust: ['Vendeur opère, client consulte', 'Sans carte physique', 'Sans application client', 'PIN sécurisé par vendeur', '30 jours d\'essai', 'International']
    },
    storeScene: {
      eyebrow: 'En situation réelle',
      title: 'Le client tape son numéro.\nLe vendeur valide la vente.',
      subtitle: 'Aucune carte. Aucune application. Le client saisit son numéro sur le terminal, voit ses points — le vendeur garde le contrôle total de la caisse.',
      floatPin: 'Client saisit son numéro',
      floatPinNum: '+1 (555) 012-3456',
      floatReward: 'Récompense activée',
      floatRewardValue: '10 € offerts · 200 pts',
      floatVendor: 'Vendeur valide',
      floatVendorSub: 'Contrôle total côté caisse'
    },
    howItWorks: {
      eyebrow: 'Fonctionnement',
      title: 'Deux écrans,\nun seul programme.',
      subtitle: 'Le terminal client et la caisse vendeur fonctionnent ensemble. Chacun a son rôle — et le vendeur garde toujours le contrôle.',
      steps: [
        { num: '01', title: 'Le client saisit son numéro', desc: 'Sur le terminal placé face à lui. Aucune carte, aucune application. Loyzen normalise en E.164.', role: 'client' },
        { num: '02', title: 'Le terminal affiche ses points', desc: 'Solde, récompenses disponibles, progression. Le client consulte — il ne peut pas modifier la vente.', role: 'client' },
        { num: '03', title: 'Le vendeur saisit le montant', desc: 'Dans sa caisse. Il voit le client identifié, propose une récompense si disponible.', role: 'vendor' },
        { num: '04', title: 'Le vendeur valide la vente', desc: 'Seul le vendeur confirme. Les points sont calculés. Le terminal se met à jour.', role: 'vendor' }
      ],
      clientLabel: 'Client',
      vendorLabel: 'Vendeur'
    },
    features: {
      eyebrow: 'Fonctionnalités',
      title: 'Tout ce dont vous\navez besoin.',
      cards: [
        { title: 'Caisse vendeur', desc: 'Le vendeur s\'authentifie avec son PIN, voit le client identifié depuis le terminal, saisit le montant, applique la récompense et valide. Contrôle total.', icon: 'pos', size: 'large', href: '/features' },
        { title: 'Terminal client', desc: 'Saisie du numéro, consultation des points et récompenses. Le client ne valide jamais la vente.', icon: 'kiosk', size: 'small', href: '/features' },
        { title: 'Récompenses', desc: 'Remise %, remise fixe, produit offert, avantage personnalisé. 4 types réels.', icon: 'reward', size: 'small', href: '/features' },
        { title: 'Équipe & PIN', desc: 'Chaque vendeur a son PIN. Rôles distincts, révocation instantanée.', icon: 'team', size: 'small', href: '/features' },
        { title: 'Pilotage', desc: 'CA fidélisé, transactions, nouveaux clients, points utilisés — en temps réel.', icon: 'dashboard', size: 'small', href: '/features' },
        { title: 'International', desc: 'FR · EN · ES. EUR, USD, GBP, CHF, CAD. Indicatifs mondiaux.', icon: 'globe', size: 'wide', href: '/features' }
      ]
    },
    onboarding: {
      eyebrow: 'Démarrage',
      title: 'Opérationnel\nen 5 étapes.',
      steps: [
        { n: 1, label: 'Configurer les règles de points' },
        { n: 2, label: 'Créer une récompense' },
        { n: 3, label: 'Ajouter un vendeur et son PIN' },
        { n: 4, label: 'Configurer et appairer un terminal' },
        { n: 5, label: 'Réaliser une vente test' }
      ]
    },
    pricing: {
      eyebrow: 'Tarifs',
      title: 'Un tarif simple.\nTout inclus.',
      monthly: 'Mensuel',
      yearly: 'Annuel',
      saving: '2 mois offerts',
      monthlyPrice: '19',
      yearlyPrice: '190',
      monthlyPeriod: '€ HT / mois',
      yearlyPeriod: '€ HT / an',
      monthlyNote: 'Facturation mensuelle · Sans engagement',
      yearlyNote: 'Facturation annuelle · Économisez 38 €',
      features: [
        'Un point de vente', 'Vendeurs illimités', 'Transactions illimitées',
        'Récompenses illimitées', 'Terminal client inclus', 'Tableau de bord temps réel',
        'Français, anglais, espagnol', 'Assistance par email', '30 jours d\'essai gratuit'
      ],
      cta: 'Commencer gratuitement',
      ctaNote: 'Aucune carte bancaire requise',
      trialNote: '30 jours d\'essai · Puis 19 € HT/mois'
    },
    faq: {
      eyebrow: 'FAQ',
      title: 'Questions fréquentes',
      items: [
        { q: 'Le client doit-il télécharger une application ?', a: 'Non. Le client saisit simplement son numéro de téléphone sur le terminal placé face à lui. Aucune installation, aucun compte client.' },
        { q: 'Qui contrôle la vente ?', a: 'Le vendeur. Toujours. Il saisit le montant, applique la récompense et confirme la vente. Le client ne peut que consulter ses points sur le terminal.' },
        { q: 'Que peut faire le client sur le terminal ?', a: 'Saisir son numéro, consulter son solde de points, voir ses récompenses disponibles et sa progression. Il ne peut pas modifier le montant ni valider la vente.' },
        { q: 'Le client peut-il valider une vente ?', a: 'Non. Seul le vendeur peut saisir le montant, appliquer une récompense et confirmer la vente. Le terminal est uniquement un écran de consultation et d\'identification.' },
        { q: 'Peut-on utiliser Loyzen sans terminal client ?', a: 'Oui. Le vendeur peut saisir lui-même le numéro du client dans la caisse et réaliser toute la vente. Le terminal est optionnel.' },
        { q: 'Comment le vendeur applique-t-il une récompense ?', a: 'Après identification du client, le vendeur voit les récompenses disponibles dans sa caisse. Il choisit et valide l\'application avec l\'accord oral du client.' },
        { q: 'Quelles récompenses peut-on créer ?', a: 'Quatre types : remise en pourcentage, remise fixe, produit offert, avantage personnalisé. Vous définissez le seuil et la valeur.' },
        { q: 'Que se passe-t-il après l\'essai ?', a: 'Après 30 jours, vous choisissez 19 € HT/mois ou 190 € HT/an. Aucune carte bancaire n\'est requise pour commencer.' }
      ]
    },
    finalCta: {
      title: 'Lancez votre programme\ngratuitement.',
      subtitle: 'Aucune carte bancaire requise. Ensuite, 19 € HT/mois.',
      cta: 'Essayer Loyzen gratuitement',
      secondary: 'Se connecter'
    }
  },
  en: {
    hero: {
      badge: 'Loyalty program · No card · No app',
      title: 'Customer loyalty,\nno card\nand no app.',
      subtitle: 'The seller keeps full control of every sale. The customer enters their number on the terminal, views their points — that\'s it.',
      cta: 'Try free for 30 days',
      ctaNote: '€19/month or €190/year · No credit card',
      ctaSecondary: 'See how it works',
      trust: ['Vendor operates, client consults', 'No physical card', 'No customer app', 'Secure PIN per seller', '30-day trial', 'International']
    },
    storeScene: {
      eyebrow: 'Real store scenario',
      title: 'Customer taps their number.\nSeller validates the sale.',
      subtitle: 'No card. No app. The customer enters their number on the terminal, sees their points — the seller keeps full control at the POS.',
      floatPin: 'Customer enters number',
      floatPinNum: '+1 (555) 012-3456',
      floatReward: 'Reward activated',
      floatRewardValue: '€10 reward · 200 pts',
      floatVendor: 'Seller validates',
      floatVendorSub: 'Full control at POS'
    },
    howItWorks: {
      eyebrow: 'How it works',
      title: 'Two screens,\none program.',
      subtitle: 'The customer terminal and seller POS work together. Each has its role — and the seller always keeps control.',
      steps: [
        { num: '01', title: 'Customer enters their number', desc: 'On the terminal facing them. No card, no app. Loyzen normalizes to E.164.', role: 'client' },
        { num: '02', title: 'Terminal shows their points', desc: 'Balance, available rewards, progress. The customer views — they cannot modify the sale.', role: 'client' },
        { num: '03', title: 'Seller enters the amount', desc: 'In their POS. They see the identified customer, offer a reward if available.', role: 'vendor' },
        { num: '04', title: 'Seller validates the sale', desc: 'Only the seller confirms. Points are calculated. The terminal updates.', role: 'vendor' }
      ],
      clientLabel: 'Customer',
      vendorLabel: 'Seller'
    },
    features: {
      eyebrow: 'Features',
      title: 'Everything you\nneed.',
      cards: [
        { title: 'Seller POS', desc: 'The seller authenticates with their PIN, sees the customer identified from the terminal, enters the amount, applies the reward and validates. Full control.', icon: 'pos', size: 'large', href: '/features' },
        { title: 'Customer terminal', desc: 'Number entry, points and rewards view. The customer never validates the sale.', icon: 'kiosk', size: 'small', href: '/features' },
        { title: 'Rewards', desc: '% discount, fixed discount, free product, custom benefit. 4 real types.', icon: 'reward', size: 'small', href: '/features' },
        { title: 'Team & PIN', desc: 'Each seller has their PIN. Distinct roles, instant revocation.', icon: 'team', size: 'small', href: '/features' },
        { title: 'Analytics', desc: 'Loyal revenue, transactions, new customers, points redeemed — real time.', icon: 'dashboard', size: 'small', href: '/features' },
        { title: 'International', desc: 'FR · EN · ES. EUR, USD, GBP, CHF, CAD. Global dial codes.', icon: 'globe', size: 'wide', href: '/features' }
      ]
    },
    onboarding: {
      eyebrow: 'Getting started',
      title: 'Live in\n5 steps.',
      steps: [
        { n: 1, label: 'Configure points rules' },
        { n: 2, label: 'Create a reward' },
        { n: 3, label: 'Add a seller and their PIN' },
        { n: 4, label: 'Configure and pair a terminal' },
        { n: 5, label: 'Run a test sale' }
      ]
    },
    pricing: {
      eyebrow: 'Pricing',
      title: 'One simple price.\nEverything included.',
      monthly: 'Monthly',
      yearly: 'Yearly',
      saving: '2 months free',
      monthlyPrice: '19',
      yearlyPrice: '190',
      monthlyPeriod: '€ / month',
      yearlyPeriod: '€ / year',
      monthlyNote: 'Monthly billing · No commitment',
      yearlyNote: 'Annual billing · Save €38',
      features: [
        'One point of sale', 'Unlimited sellers', 'Unlimited transactions',
        'Unlimited rewards', 'Customer terminal included', 'Real-time dashboard',
        'French, English, Spanish', 'Email support', '30-day free trial'
      ],
      cta: 'Start free',
      ctaNote: 'No credit card required',
      trialNote: '30-day trial · Then €19/month'
    },
    faq: {
      eyebrow: 'FAQ',
      title: 'Frequently asked questions',
      items: [
        { q: 'Does the customer need to download an app?', a: 'No. The customer simply enters their phone number on the terminal. No installation, no customer account.' },
        { q: 'Who controls the sale?', a: 'The seller. Always. They enter the amount, apply the reward, and confirm the sale. The customer can only view their points on the terminal.' },
        { q: 'What can the customer do on the terminal?', a: 'Enter their number, view their points balance, see available rewards and progress. They cannot modify the amount or validate the sale.' },
        { q: 'Can the customer validate a sale?', a: 'No. Only the seller can enter the amount, apply a reward, and confirm the sale. The terminal is only a consultation and identification screen.' },
        { q: 'Can Loyzen be used without a customer terminal?', a: 'Yes. The seller can enter the customer\'s number in the POS and complete the entire sale. The terminal is optional.' },
        { q: 'How does the seller apply a reward?', a: 'After customer identification, the seller sees available rewards in their POS. They choose and validate the application with the customer\'s verbal agreement.' },
        { q: 'What rewards can be created?', a: 'Four types: percentage discount, fixed discount, free product, custom benefit. You define the threshold and value.' },
        { q: 'What happens after the trial?', a: 'After 30 days, choose €19/month or €190/year. No credit card required to start.' }
      ]
    },
    finalCta: {
      title: 'Launch your program\nfor free.',
      subtitle: 'No credit card required. Then €19/month.',
      cta: 'Try Loyzen for free',
      secondary: 'Sign in'
    }
  },
  es: {
    hero: {
      badge: 'Programa de fidelización · Sin tarjeta · Sin app',
      title: 'La fidelización,\nsin tarjeta\ny sin aplicación.',
      subtitle: 'El vendedor mantiene el control total de cada venta. El cliente ingresa su número en el terminal, consulta sus puntos — y eso es todo.',
      cta: 'Probar gratis 30 días',
      ctaNote: '19 €/mes o 190 €/año · Sin tarjeta de crédito',
      ctaSecondary: 'Ver cómo funciona',
      trust: ['Vendedor opera, cliente consulta', 'Sin tarjeta física', 'Sin app del cliente', 'PIN seguro por vendedor', '30 días de prueba', 'Internacional']
    },
    storeScene: {
      eyebrow: 'Escenario real en tienda',
      title: 'El cliente ingresa su número.\nEl vendedor valida la venta.',
      subtitle: 'Sin tarjeta. Sin app. El cliente ingresa su número en el terminal, ve sus puntos — el vendedor mantiene el control total en caja.',
      floatPin: 'Cliente ingresa número',
      floatPinNum: '+1 (555) 012-3456',
      floatReward: 'Recompensa activada',
      floatRewardValue: '10 € de regalo · 200 pts',
      floatVendor: 'Vendedor valida',
      floatVendorSub: 'Control total en caja'
    },
    howItWorks: {
      eyebrow: 'Cómo funciona',
      title: 'Dos pantallas,\nun programa.',
      subtitle: 'El terminal del cliente y la caja del vendedor trabajan juntos. Cada uno tiene su rol — y el vendedor siempre mantiene el control.',
      steps: [
        { num: '01', title: 'El cliente ingresa su número', desc: 'En el terminal frente a él. Sin tarjeta, sin app. Loyzen normaliza a E.164.', role: 'client' },
        { num: '02', title: 'El terminal muestra sus puntos', desc: 'Saldo, recompensas disponibles, progreso. El cliente consulta — no puede modificar la venta.', role: 'client' },
        { num: '03', title: 'El vendedor ingresa el monto', desc: 'En su caja. Ve al cliente identificado, ofrece una recompensa si está disponible.', role: 'vendor' },
        { num: '04', title: 'El vendedor valida la venta', desc: 'Solo el vendedor confirma. Se calculan los puntos. El terminal se actualiza.', role: 'vendor' }
      ],
      clientLabel: 'Cliente',
      vendorLabel: 'Vendedor'
    },
    features: {
      eyebrow: 'Funcionalidades',
      title: 'Todo lo que\nnecesitas.',
      cards: [
        { title: 'Caja vendedor', desc: 'El vendedor se autentica con su PIN, ve al cliente identificado desde el terminal, ingresa el monto, aplica la recompensa y valida. Control total.', icon: 'pos', size: 'large', href: '/features' },
        { title: 'Terminal cliente', desc: 'Ingreso de número, consulta de puntos y recompensas. El cliente nunca valida la venta.', icon: 'kiosk', size: 'small', href: '/features' },
        { title: 'Recompensas', desc: 'Descuento %, descuento fijo, producto gratis, beneficio personalizado. 4 tipos reales.', icon: 'reward', size: 'small', href: '/features' },
        { title: 'Equipo & PIN', desc: 'Cada vendedor tiene su PIN. Roles distintos, revocación instantánea.', icon: 'team', size: 'small', href: '/features' },
        { title: 'Control', desc: 'Ingresos fidelizados, transacciones, nuevos clientes, puntos usados — tiempo real.', icon: 'dashboard', size: 'small', href: '/features' },
        { title: 'Internacional', desc: 'FR · EN · ES. EUR, USD, GBP, CHF, CAD. Indicativos mundiales.', icon: 'globe', size: 'wide', href: '/features' }
      ]
    },
    onboarding: {
      eyebrow: 'Inicio',
      title: 'Operativo\nen 5 pasos.',
      steps: [
        { n: 1, label: 'Configurar reglas de puntos' },
        { n: 2, label: 'Crear una recompensa' },
        { n: 3, label: 'Agregar un vendedor y su PIN' },
        { n: 4, label: 'Configurar y vincular un terminal' },
        { n: 5, label: 'Realizar una venta de prueba' }
      ]
    },
    pricing: {
      eyebrow: 'Precios',
      title: 'Un precio simple.\nTodo incluido.',
      monthly: 'Mensual',
      yearly: 'Anual',
      saving: '2 meses gratis',
      monthlyPrice: '19',
      yearlyPrice: '190',
      monthlyPeriod: '€ / mes',
      yearlyPeriod: '€ / año',
      monthlyNote: 'Facturación mensual · Sin compromiso',
      yearlyNote: 'Facturación anual · Ahorra 38 €',
      features: [
        'Un punto de venta', 'Vendedores ilimitados', 'Transacciones ilimitadas',
        'Recompensas ilimitadas', 'Terminal cliente incluido', 'Panel en tiempo real',
        'Francés, inglés, español', 'Soporte por email', '30 días de prueba gratis'
      ],
      cta: 'Comenzar gratis',
      ctaNote: 'Sin tarjeta de crédito',
      trialNote: '30 días de prueba · Luego 19 €/mes'
    },
    faq: {
      eyebrow: 'FAQ',
      title: 'Preguntas frecuentes',
      items: [
        { q: '¿El cliente necesita descargar una app?', a: 'No. El cliente simplemente ingresa su número en el terminal. Sin instalación, sin cuenta de cliente.' },
        { q: '¿Quién controla la venta?', a: 'El vendedor. Siempre. Ingresa el monto, aplica la recompensa y confirma la venta. El cliente solo puede consultar sus puntos en el terminal.' },
        { q: '¿Qué puede hacer el cliente en el terminal?', a: 'Ingresar su número, ver su saldo de puntos, ver recompensas disponibles y progreso. No puede modificar el monto ni validar la venta.' },
        { q: '¿Puede el cliente validar una venta?', a: 'No. Solo el vendedor puede ingresar el monto, aplicar una recompensa y confirmar la venta. El terminal es solo una pantalla de consulta e identificación.' },
        { q: '¿Se puede usar Loyzen sin terminal cliente?', a: 'Sí. El vendedor puede ingresar el número del cliente en la caja y completar toda la venta. El terminal es opcional.' },
        { q: '¿Cómo aplica el vendedor una recompensa?', a: 'Tras identificar al cliente, el vendedor ve las recompensas disponibles en su caja. Elige y valida la aplicación con el acuerdo verbal del cliente.' },
        { q: '¿Qué recompensas se pueden crear?', a: 'Cuatro tipos: descuento en porcentaje, descuento fijo, producto gratis, beneficio personalizado.' },
        { q: '¿Qué pasa después del período de prueba?', a: 'Después de 30 días, elige 19 €/mes o 190 €/año. Sin tarjeta de crédito para empezar.' }
      ]
    },
    finalCta: {
      title: 'Lanza tu programa\ngratis.',
      subtitle: 'Sin tarjeta de crédito. Luego 19 €/mes.',
      cta: 'Probar Loyzen gratis',
      secondary: 'Iniciar sesión'
    }
  }
};

// ─── PICTOGRAM ────────────────────────────────────────────────────────────────
function LoyzenPicto({ size = 32 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <circle cx="18" cy="18" r="15" stroke="#70F59A" strokeWidth="1.8" strokeDasharray="4 2.5" opacity="0.4" />
      <line x1="18" y1="12.5" x2="18" y2="5" stroke="#70F59A" strokeWidth="2.5" strokeLinecap="round" opacity="0.85" />
      <line x1="21" y1="20" x2="28" y2="29" stroke="#70F59A" strokeWidth="2.5" strokeLinecap="round" opacity="0.85" />
      <line x1="15" y1="20" x2="8" y2="29" stroke="#70F59A" strokeWidth="2.5" strokeLinecap="round" opacity="0.85" />
      <circle cx="18" cy="3.5" r="3.5" fill="#70F59A" />
      <circle cx="30" cy="30.5" r="3.5" fill="#70F59A" />
      <circle cx="6" cy="30.5" r="3.5" fill="#70F59A" />
      <circle cx="18" cy="18" r="6" fill="#70F59A" />
      <circle cx="18" cy="18" r="2.8" fill="#d1fae5" opacity="0.95" />
    </svg>
  );
}

// ─── ICONS ────────────────────────────────────────────────────────────────────
function Icon({ name, size = 18 }: { name: string; size?: number }) {
  const s = size;
  const icons: Record<string, React.ReactNode> = {
    phone: <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" /><line x1="12" y1="18" x2="12.01" y2="18" /></svg>,
    pos: <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>,
    kiosk: <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="7" y="2" width="10" height="20" rx="2" /><line x1="12" y1="18" x2="12.01" y2="18" /></svg>,
    reward: <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6" /><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11" /></svg>,
    team: <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
    dashboard: <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>,
    globe: <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>,
    check: <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>,
    arrowRight: <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>,
    chevronDown: <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>,
    shield: <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
    zap: <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>,
  };
  return <>{icons[name] || null}</>;
}

// ─── DASHBOARD MOCKUP ─────────────────────────────────────────────────────────
function LiveDashboard() {
  const [activeStep, setActiveStep] = useState(0);
  const [typedDigits, setTypedDigits] = useState('');
  const [showReward, setShowReward] = useState(false);
  const [activeRow, setActiveRow] = useState(0);
  const [activeTab, setActiveTab] = useState(0);

  const sparkCA = [28, 35, 31, 42, 38, 55, 48, 62, 58, 71, 68, 82];
  const sparkTx = [12, 18, 14, 22, 19, 28, 24, 31, 27, 35, 32, 40];
  const sparkPts = [180, 220, 195, 260, 240, 310, 285, 340, 320, 390, 365, 420];
  const barData = [22, 35, 28, 48, 38, 62, 44, 72, 55, 80, 68, 76];

  const delayFn = (ms: number) => new Promise((r) => setTimeout(r, ms));

  useEffect(() => {
    let cancelled = false;
    const sequence = async () => {
      if (cancelled) return;
      await delayFn(1200);
      if (cancelled) return;
      setActiveStep(1);
      const digits = '+1 (555) 012';
      for (let i = 0; i < digits.length; i++) {
        await delayFn(100);
        if (cancelled) return;
        setTypedDigits(digits.slice(0, i + 1));
      }
      await delayFn(600);
      if (cancelled) return;
      setActiveStep(2);
      await delayFn(1200);
      if (cancelled) return;
      setActiveStep(3);
      setShowReward(true);
      await delayFn(2600);
      if (cancelled) return;
      setActiveStep(0);
      setTypedDigits('');
      setShowReward(false);
    };
    sequence();
    const loop = setInterval(sequence, 8000);
    return () => { cancelled = true; clearInterval(loop); };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setActiveRow((r) => (r + 1) % 4), 2000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setActiveTab((t) => (t + 1) % 3), 4000);
    return () => clearInterval(interval);
  }, []);

  const transactions = [
    { name: 'Marie D.', amount: '54 €', pts: '+27 pts', time: '14:32', reward: false, avatar: 'M' },
    { name: 'Thomas L.', amount: '89 €', pts: '+44 pts', time: '14:28', reward: false, avatar: 'T' },
    { name: 'Sophie M.', amount: '32 €', pts: '+16 pts', time: '14:15', reward: false, avatar: 'S' },
    { name: 'Lucas B.', amount: '127 €', pts: '🎁 Récompense', time: '13:58', reward: true, avatar: 'L' }
  ];

  const tabs = ['Vue d\'ensemble', 'Transactions', 'Clients'];

  function Sparkline({ data, color }: { data: number[]; color: string }) {
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const w = 56, h = 20;
    const pts = data.map((v, i) => {
      const x = (i / (data.length - 1)) * w;
      const y = h - ((v - min) / range) * h;
      return `${x},${y}`;
    }).join(' ');
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} fill="none">
        <polyline points={pts} stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.8" />
      </svg>
    );
  }

  return (
    <div className="relative w-full mx-auto" style={{ maxWidth: '720px' }}>
      <div className="absolute -inset-16 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 60%, rgba(112,245,154,0.06) 0%, transparent 65%)' }} />
      <div
        className="relative rounded-2xl overflow-hidden"
        style={{
          background: 'var(--lz-card)',
          border: '1px solid var(--lz-border)',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.5), 0 40px 100px rgba(0,0,0,0.9), 0 0 80px rgba(112,245,154,0.04)'
        }}
      >
        {/* macOS title bar */}
        <div className="flex items-center justify-between px-4 py-3" style={{ background: 'var(--lz-raised)', borderBottom: '1px solid var(--lz-border)' }}>
          <div className="flex items-center gap-3">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full" style={{ background: '#ff5f57' }} />
              <div className="w-3 h-3 rounded-full" style={{ background: '#febc2e' }} />
              <div className="w-3 h-3 rounded-full" style={{ background: '#28c840' }} />
            </div>
            <div className="flex items-center gap-2 px-3 py-1 rounded-md" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--lz-border)', minWidth: '200px' }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#52525b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span className="text-[10px] font-mono" style={{ color: '#52525b' }}>app.loyzen.io/dashboard</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-md" style={{ background: 'rgba(112,245,154,0.08)', border: '1px solid rgba(112,245,154,0.15)' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-[#70F59A] animate-pulse" />
            <span className="text-[9px] font-mono font-semibold" style={{ color: 'var(--lz-green)' }}>LIVE</span>
          </div>
        </div>

        {/* App shell */}
        <div className="flex" style={{ minHeight: '420px' }}>
          {/* Sidebar */}
          <div className="flex flex-col gap-1 py-4 px-2 shrink-0" style={{ width: '48px', background: 'var(--lz-card)', borderRight: '1px solid var(--lz-border)' }}>
            {[
              { icon: 'dashboard', active: true },
              { icon: 'pos', active: false },
              { icon: 'team', active: false },
              { icon: 'reward', active: false },
              { icon: 'globe', active: false },
            ].map((item, i) => (
              <div key={i} className="w-8 h-8 rounded-lg flex items-center justify-center mx-auto" style={{
                background: item.active ? 'rgba(112,245,154,0.1)' : 'transparent',
                color: item.active ? 'var(--lz-green)' : '#3f3f46',
                border: item.active ? '1px solid rgba(112,245,154,0.2)' : '1px solid transparent'
              }}>
                <Icon name={item.icon} size={14} />
              </div>
            ))}
          </div>

          {/* Main content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: '1px solid var(--lz-border)' }}>
              <div>
                <div className="text-[13px] font-semibold" style={{ color: 'var(--lz-text-primary)', letterSpacing: '-0.02em' }}>Tableau de bord</div>
                <div className="text-[10px] font-mono mt-0.5" style={{ color: '#52525b' }}>Commerce · Alice · Terminal T-01</div>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-mono" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--lz-border)', color: '#71717a' }}>
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                Juil 2026
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-0 px-5 pt-3" style={{ borderBottom: '1px solid var(--lz-border)' }}>
              {tabs.map((tab, i) => (
                <button key={i} className="px-3 pb-2.5 text-[11px] font-medium transition-colors relative" style={{ color: activeTab === i ? 'var(--lz-text-primary)' : '#52525b' }}>
                  {tab}
                  {activeTab === i && <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'var(--lz-green)' }} />}
                </button>
              ))}
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-4 gap-0 px-4 pt-4 pb-3">
              {[
                { label: 'CA fidélisé', value: '4 280 €', trend: '+12%', spark: sparkCA },
                { label: 'Transactions', value: '312', trend: '+8%', spark: sparkTx },
                { label: 'Nouveaux clients', value: '47', trend: '+23%', spark: sparkCA },
                { label: 'Points utilisés', value: '8 640', trend: '+5%', spark: sparkPts },
              ].map((kpi, i) => (
                <div key={i} className="px-3 py-2.5 rounded-xl mx-1" style={{ background: 'var(--lz-raised)', border: '1px solid var(--lz-border)' }}>
                  <div className="flex items-start justify-between mb-1.5">
                    <div className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: '#52525b', letterSpacing: '0.08em' }}>{kpi.label}</div>
                    <Sparkline data={kpi.spark} color="var(--lz-green)" />
                  </div>
                  <div className="text-[15px] font-semibold" style={{ color: 'var(--lz-text-primary)', letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums' }}>{kpi.value}</div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="var(--lz-green)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></svg>
                    <span className="text-[9px] font-mono font-semibold" style={{ color: 'var(--lz-green)' }}>{kpi.trend}</span>
                    <span className="text-[9px] font-mono" style={{ color: '#3f3f46' }}>vs mois préc.</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Chart + Transactions */}
            <div className="flex gap-3 px-4 pb-4 flex-1">
              <div className="flex-1 rounded-xl p-3" style={{ background: 'var(--lz-raised)', border: '1px solid var(--lz-border)' }}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#52525b', letterSpacing: '0.08em' }}>CA mensuel</span>
                  <span className="text-[9px] font-mono" style={{ color: 'var(--lz-green)' }}>2026</span>
                </div>
                <div className="flex items-end gap-1" style={{ height: '64px' }}>
                  {barData.map((h, i) => (
                    <div key={i} className="flex-1 rounded-sm transition-all duration-300" style={{
                      height: `${h}%`,
                      background: i >= 10 ? 'linear-gradient(to top, #16a34a, var(--lz-green))' : i === 9 ? 'rgba(112,245,154,0.25)' : 'rgba(255,255,255,0.06)',
                    }} />
                  ))}
                </div>
                <div className="flex justify-between mt-1.5">
                  {['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'].map((m, i) => (
                    <span key={i} className="text-[7px] font-mono" style={{ color: i >= 10 ? 'var(--lz-green)' : '#2d2d35' }}>{m}</span>
                  ))}
                </div>
              </div>

              {/* Recent transactions */}
              <div className="rounded-xl p-3" style={{ width: '200px', background: 'var(--lz-raised)', border: '1px solid var(--lz-border)' }}>
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#52525b', letterSpacing: '0.08em' }}>Récentes</span>
                  <span className="flex items-center gap-1 text-[9px] font-mono" style={{ color: 'var(--lz-green)' }}>
                    <span className="w-1 h-1 rounded-full bg-[#70F59A] animate-pulse" />LIVE
                  </span>
                </div>
                <div className="space-y-1.5">
                  {transactions.map((tx, i) => (
                    <div key={i} className="flex items-center justify-between py-1 px-1.5 rounded-lg transition-all duration-500" style={{
                      background: activeRow === i ? 'rgba(112,245,154,0.06)' : 'transparent',
                      border: activeRow === i ? '1px solid rgba(112,245,154,0.1)' : '1px solid transparent'
                    }}>
                      <div className="flex items-center gap-1.5">
                        <div className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-semibold shrink-0" style={{ background: 'rgba(255,255,255,0.06)', color: '#71717a' }}>{tx.avatar}</div>
                        <div>
                          <div className="text-[9px] font-medium" style={{ color: '#a1a1aa' }}>{tx.name}</div>
                          <div className="text-[8px] font-mono" style={{ color: '#3f3f46' }}>{tx.time}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[9px] font-mono font-semibold" style={{ color: 'var(--lz-text-primary)' }}>{tx.amount}</div>
                        <div className="text-[8px] font-mono" style={{ color: tx.reward ? 'var(--lz-green-bright)' : 'var(--lz-green)' }}>{tx.pts}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Status bar */}
        <div className="flex items-center justify-between px-4 py-2" style={{ background: 'var(--lz-deep)', borderTop: '1px solid var(--lz-border)' }}>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 text-[9px] font-mono" style={{ color: '#3f3f46' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#70F59A]" />Terminal T-01 connecté
            </span>
            <span className="text-[9px] font-mono" style={{ color: '#2d2d35' }}>·</span>
            <span className="text-[9px] font-mono" style={{ color: '#3f3f46' }}>Vendeur: Alice</span>
          </div>
          <span className="text-[9px] font-mono" style={{ color: '#2d2d35' }}>Démo · données fictives</span>
        </div>

        {/* Floating card — phone input */}
        <div className={`absolute -left-6 bottom-24 hidden sm:block transition-all duration-500 ${activeStep >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
          <div className="rounded-2xl px-4 py-3 shadow-2xl" style={{
            background: 'var(--lz-raised)',
            border: `1px solid ${activeStep >= 1 ? 'rgba(112,245,154,0.25)' : 'var(--lz-border)'}`,
            boxShadow: '0 8px 32px rgba(0,0,0,0.8)'
          }}>
            <div className="flex items-center gap-1.5 mb-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#70F59A] animate-pulse" />
              <span className="text-[9px] font-mono font-semibold uppercase tracking-wider" style={{ color: 'var(--lz-green)' }}>
                {activeStep === 0 ? 'Terminal client' : activeStep === 1 ? 'Saisie en cours…' : '✓ Identifié'}
              </span>
            </div>
            <div className="text-[13px] font-mono font-semibold" style={{ color: 'var(--lz-text-primary)', minWidth: '130px', letterSpacing: '0.02em' }}>
              {activeStep === 1 ? typedDigits + '|' : activeStep >= 2 ? '+1 (555) ••••' : '_ _ _ _ _ _'}
            </div>
          </div>
        </div>

        {/* Floating card — reward */}
        <div className={`absolute -right-6 top-20 hidden sm:block transition-all duration-700 ${showReward ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-1'}`}>
          <div className="rounded-2xl px-4 py-3 shadow-2xl" style={{
            background: 'var(--lz-raised)',
            border: '1px solid rgba(112,245,154,0.3)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.8), 0 0 20px rgba(112,245,154,0.08)'
          }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center text-lg shrink-0" style={{ background: 'rgba(112,245,154,0.1)', border: '1px solid rgba(112,245,154,0.2)' }}>🎁</div>
              <div>
                <div className="text-[11px] font-semibold" style={{ color: 'var(--lz-text-primary)', letterSpacing: '-0.01em' }}>Récompense · 10 € activée</div>
                <div className="text-[9px] font-mono mt-0.5" style={{ color: 'var(--lz-green)' }}>Marie D. · 200 pts atteints</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── STORE SCENE ─────────────────────────────────────────────────────────────
function StoreScene({ t }: { t: typeof C['fr']['storeScene'] }) {
  const [rewardVisible, setRewardVisible] = useState(false);
  const [pinVisible, setPinVisible] = useState(false);
  const [vendorVisible, setVendorVisible] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setPinVisible(true), 600);
    const t2 = setTimeout(() => setRewardVisible(true), 1800);
    const t3 = setTimeout(() => setVendorVisible(true), 3000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  return (
    <div className="relative w-full mx-auto">
      <div className="absolute -inset-8 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(112,245,154,0.04) 0%, transparent 70%)' }} />
      <div className="relative rounded-2xl overflow-hidden" style={{ border: '1px solid var(--lz-border)', boxShadow: '0 48px 120px rgba(0,0,0,0.9)' }}>
        <img
          src="https://images.unsplash.com/photo-1712331393877-995589f20106"
          alt="Client heureux à la caisse d'un commerce, souriant en voyant sa récompense s'afficher sur le terminal pendant que le vendeur valide la vente"
          className="w-full object-cover"
          style={{ height: 'clamp(320px, 45vw, 600px)', filter: 'brightness(0.65) saturate(0.8)' }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(5,5,5,0.9) 0%, rgba(5,5,5,0.15) 50%, transparent 100%)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(5,5,5,0.35) 0%, transparent 50%, rgba(5,5,5,0.2) 100%)' }} />

        {/* Floating overlay — Client enters number */}
        <div className={`absolute top-6 left-6 sm:top-8 sm:left-8 transition-all duration-700 ${pinVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3'}`} style={{ animation: pinVisible ? 'float 6s ease-in-out infinite' : 'none' }}>
          <div className="rounded-2xl px-4 py-3 backdrop-blur-xl" style={{ background: 'rgba(9,10,9,0.92)', border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 8px 32px rgba(0,0,0,0.7)' }}>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#70F59A] animate-pulse" />
              <span className="text-[10px] font-mono font-semibold uppercase tracking-wider" style={{ color: 'var(--lz-green)' }}>{t.floatPin}</span>
            </div>
            <div className="text-[16px] font-mono font-semibold" style={{ color: 'var(--lz-text-primary)', letterSpacing: '0.02em' }}>{t.floatPinNum}</div>
            <div className="mt-1.5 flex items-center gap-1.5">
              <div className="h-1 rounded-full flex-1" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div className="h-1 rounded-full w-3/4" style={{ background: 'linear-gradient(to right, #16a34a, var(--lz-green))' }} />
              </div>
              <span className="text-[9px] font-mono" style={{ color: '#52525b' }}>200 pts</span>
            </div>
          </div>
        </div>

        {/* Floating overlay — Reward activated */}
        <div className={`absolute top-6 right-6 sm:top-8 sm:right-8 transition-all duration-700 delay-300 ${rewardVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3'}`} style={{ animation: rewardVisible ? 'float 7s ease-in-out 1s infinite' : 'none' }}>
          <div className="rounded-2xl px-4 py-3 backdrop-blur-xl" style={{ background: 'rgba(9,10,9,0.92)', border: '1px solid rgba(112,245,154,0.3)', boxShadow: '0 8px 32px rgba(0,0,0,0.7), 0 0 24px rgba(112,245,154,0.12)' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0" style={{ background: 'rgba(112,245,154,0.12)', border: '1px solid rgba(112,245,154,0.25)' }}>🎁</div>
              <div>
                <div className="text-[12px] font-semibold" style={{ color: 'var(--lz-text-primary)', letterSpacing: '-0.01em' }}>{t.floatReward}</div>
                <div className="text-[11px] font-mono mt-0.5" style={{ color: 'var(--lz-green)' }}>{t.floatRewardValue}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Floating overlay — Vendor validates */}
        <div className={`absolute bottom-8 right-6 sm:bottom-10 sm:right-8 transition-all duration-700 delay-500 ${vendorVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`} style={{ animation: vendorVisible ? 'float 8s ease-in-out 2s infinite' : 'none' }}>
          <div className="rounded-2xl px-4 py-3 backdrop-blur-xl" style={{ background: 'rgba(9,10,9,0.92)', border: '1px solid rgba(96,165,250,0.25)', boxShadow: '0 8px 32px rgba(0,0,0,0.7), 0 0 20px rgba(59,130,246,0.1)' }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(96,165,250,0.25)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              </div>
              <div>
                <div className="text-[12px] font-semibold" style={{ color: 'var(--lz-text-primary)', letterSpacing: '-0.01em' }}>{t.floatVendor}</div>
                <div className="text-[10px] mt-0.5" style={{ color: '#60a5fa' }}>{t.floatVendorSub}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#70F59A] animate-pulse" />
            <span className="text-[11px] font-mono uppercase tracking-wider" style={{ color: 'var(--lz-green)' }}>Démonstration</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── TERMINAL SCREEN ─────────────────────────────────────────────────────────
function TerminalScreen() {
  const [step, setStep] = useState(0);
  const [typed, setTyped] = useState('');

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const delay = (ms: number) => new Promise(r => setTimeout(r, ms));
      await delay(800);
      if (cancelled) return;
      setStep(1);
      const num = '+1 (555) 012-3456';
      for (let i = 0; i < num.length; i++) {
        await delay(90);
        if (cancelled) return;
        setTyped(num.slice(0, i + 1));
      }
      await delay(700);
      if (cancelled) return;
      setStep(2);
      await delay(3000);
      if (cancelled) return;
      setStep(3);
      await delay(2500);
      if (cancelled) return;
      setStep(0);
      setTyped('');
    };
    run();
    const loop = setInterval(run, 9000);
    return () => { cancelled = true; clearInterval(loop); };
  }, []);

  return (
    <div className="relative mx-auto" style={{ width: '220px' }}>
      <div className="absolute -inset-8 pointer-events-none" style={{ background: 'radial-gradient(ellipse, rgba(112,245,154,0.08) 0%, transparent 70%)' }} />
      <div className="relative rounded-3xl overflow-hidden" style={{ background: '#1a1a1f', padding: '10px', boxShadow: '0 0 0 1px var(--lz-border), 0 24px 60px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.06)' }}>
        <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--lz-deep)', minHeight: '340px', position: 'relative' }}>
          <div className="flex items-center justify-between px-4 py-2" style={{ background: 'var(--lz-card)', borderBottom: '1px solid var(--lz-border)' }}>
            <span className="text-[9px] font-mono" style={{ color: '#3f3f46' }}>loyzen</span>
            <div className="flex items-center gap-1">
              <span className="w-1 h-1 rounded-full bg-[#70F59A] animate-pulse" />
              <span className="text-[8px] font-mono" style={{ color: 'var(--lz-green)' }}>LIVE</span>
            </div>
          </div>
          <div className="px-4 py-5 flex flex-col items-center">
            {step === 0 && (
              <>
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(112,245,154,0.08)', border: '1px solid rgba(112,245,154,0.15)', color: 'var(--lz-green)' }}>
                  <Icon name="phone" size={20} />
                </div>
                <div className="text-[13px] font-semibold text-center mb-1" style={{ color: 'var(--lz-text-primary)', letterSpacing: '-0.02em' }}>Votre numéro</div>
                <div className="text-[10px] text-center" style={{ color: '#52525b' }}>Saisissez pour identifier</div>
                <div className="mt-5 w-full h-10 rounded-xl flex items-center px-3" style={{ background: 'var(--lz-raised)', border: '1px solid var(--lz-border)' }}>
                  <span className="text-[11px] font-mono" style={{ color: '#3f3f46' }}>+1 (555) _ _ _ - _ _ _ _</span>
                </div>
              </>
            )}
            {step === 1 && (
              <>
                <div className="text-[11px] font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--lz-green)', letterSpacing: '0.1em' }}>Saisie en cours</div>
                <div className="w-full h-10 rounded-xl flex items-center px-3 mb-1" style={{ background: 'var(--lz-raised)', border: '1px solid rgba(112,245,154,0.25)' }}>
                  <span className="text-[12px] font-mono font-semibold" style={{ color: 'var(--lz-text-primary)' }}>{typed}<span className="animate-pulse">|</span></span>
                </div>
                <div className="text-[9px] font-mono mt-1" style={{ color: '#52525b' }}>Format E.164 · Normalisation auto</div>
              </>
            )}
            {step === 2 && (
              <>
                <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ background: 'rgba(112,245,154,0.1)', border: '1px solid rgba(112,245,154,0.25)' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--lz-green)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                </div>
                <div className="text-[13px] font-semibold mb-0.5" style={{ color: 'var(--lz-text-primary)', letterSpacing: '-0.02em' }}>Marie D.</div>
                <div className="text-[10px] font-mono mb-4" style={{ color: '#52525b' }}>+1 (555) 012-3456</div>
                <div className="w-full rounded-xl p-3" style={{ background: 'var(--lz-raised)', border: '1px solid var(--lz-border)' }}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: '#52525b' }}>Points</span>
                    <span className="text-[13px] font-semibold font-mono" style={{ color: 'var(--lz-green)' }}>200 pts</span>
                  </div>
                  <div className="h-1.5 rounded-full w-full mb-2" style={{ background: 'rgba(255,255,255,0.06)' }}>
                    <div className="h-1.5 rounded-full" style={{ width: '80%', background: 'linear-gradient(to right, #16a34a, var(--lz-green))' }} />
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[8px] font-mono" style={{ color: '#3f3f46' }}>0</span>
                    <span className="text-[8px] font-mono" style={{ color: 'var(--lz-green)' }}>🎁 Récompense dispo</span>
                    <span className="text-[8px] font-mono" style={{ color: '#3f3f46' }}>250</span>
                  </div>
                </div>
              </>
            )}
            {step === 3 && (
              <>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 text-xl" style={{ background: 'rgba(112,245,154,0.1)', border: '1px solid rgba(112,245,154,0.25)' }}>🎁</div>
                <div className="text-[12px] font-semibold mb-1" style={{ color: 'var(--lz-text-primary)', letterSpacing: '-0.02em' }}>Récompense activée !</div>
                <div className="text-[11px] font-mono mb-4" style={{ color: 'var(--lz-green)' }}>10 € offerts</div>
                <div className="w-full rounded-xl p-3 text-center" style={{ background: 'rgba(112,245,154,0.06)', border: '1px solid rgba(112,245,154,0.2)' }}>
                  <div className="text-[10px]" style={{ color: '#a1a1aa' }}>Nouveau solde</div>
                  <div className="text-[16px] font-semibold font-mono mt-0.5" style={{ color: 'var(--lz-green)' }}>0 pts</div>
                  <div className="text-[9px] font-mono mt-1" style={{ color: '#52525b' }}>Vendeur confirme la vente</div>
                </div>
              </>
            )}
          </div>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-12 h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.12)' }} />
        </div>
      </div>
      <div className="absolute right-0 top-16 w-1 h-8 rounded-l-sm" style={{ background: '#2a2a2f' }} />
      <div className="absolute left-0 top-12 w-1 h-6 rounded-r-sm" style={{ background: '#2a2a2f' }} />
      <div className="absolute left-0 top-20 w-1 h-6 rounded-r-sm" style={{ background: '#2a2a2f' }} />
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function MarketingHomePage() {
  const [locale, setLocale] = useState<Locale>('fr');
  const [pricingYearly, setPricingYearly] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const saved = typeof window !== 'undefined' ? localStorage.getItem('loyzen-locale') : null;
    if (saved === 'fr' || saved === 'en' || saved === 'es') {
      setLocale(saved);
    } else {
      const lang = navigator.language.toLowerCase();
      if (lang.startsWith('es')) setLocale('es');
      else if (lang.startsWith('en')) setLocale('en');
      else setLocale('fr');
    }
  }, []);

  const handleLocaleChange = (l: Locale) => {
    setLocale(l);
    if (typeof window !== 'undefined') localStorage.setItem('loyzen-locale', l);
  };

  const t = C[locale];
  if (!mounted) return null;

  return (
    <div className="min-h-screen text-white overflow-x-hidden" style={{ background: 'var(--lz-bg)' }}>
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
      `}</style>
      <MarketingHeader locale={locale} onLocaleChange={handleLocaleChange} />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <HeroSection locale={locale} content={t.hero} />

      {/* ── STORE SCENE — asymmetric left text / right image ─────────────── */}
      <section className="relative overflow-hidden" style={{ background: 'var(--lz-deep)', borderTop: '1px solid var(--lz-border)', borderBottom: '1px solid var(--lz-border)' }}>
        {/* Subtle dot grid */}
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.025) 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        <div className="relative z-10 w-full" style={{ maxWidth: '1360px', margin: '0 auto', padding: 'clamp(80px, 8vw, 160px) clamp(24px, 4vw, 48px)' }}>
          {/* Asymmetric: 40% text / 60% image */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-12 lg:gap-20">
            {/* Left — editorial text (40%) */}
            <div className="lg:w-[38%] flex flex-col">
              <p className="lz-label mb-6">{t.storeScene.eyebrow}</p>
              <h2 className="lz-h2 whitespace-pre-line mb-6">{t.storeScene.title}</h2>
              <p className="lz-body mb-8" style={{ maxWidth: '420px' }}>{t.storeScene.subtitle}</p>
              <div className="flex flex-col gap-3">
                {[
                  { label: locale === 'fr' ? 'Client saisit son numéro' : locale === 'en' ? 'Customer enters number' : 'Cliente ingresa número', color: 'var(--lz-green)' },
                  { label: locale === 'fr' ? 'Terminal affiche points & récompenses' : locale === 'en' ? 'Terminal shows points & rewards' : 'Terminal muestra puntos y recompensas', color: 'var(--lz-green)' },
                  { label: locale === 'fr' ? 'Vendeur valide — contrôle total' : locale === 'en' ? 'Seller validates — full control' : 'Vendedor valida — control total', color: '#60a5fa' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: item.color }} />
                    <span className="text-[13px]" style={{ color: 'var(--lz-text-secondary)', fontFamily: 'var(--font-inter)' }}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Right — image (60%) */}
            <div className="lg:w-[62%]">
              <StoreScene t={t.storeScene} />
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS — asymmetric bento grid ─────────────────────────── */}
      <section id="fonctionnement" className="relative overflow-hidden" style={{ background: 'var(--lz-bg)' }}>
        <div className="relative z-10 w-full" style={{ maxWidth: '1360px', margin: '0 auto', padding: 'clamp(80px, 8vw, 160px) clamp(24px, 4vw, 48px)' }}>
          {/* Header — left aligned */}
          <div className="mb-16" style={{ maxWidth: '560px' }}>
            <p className="lz-label mb-6">{t.howItWorks.eyebrow}</p>
            <h2 className="lz-h2 whitespace-pre-line mb-6">{t.howItWorks.title}</h2>
            <p className="lz-body">{t.howItWorks.subtitle}</p>
          </div>

          {/* Asymmetric 2-col bento: client steps left (wider), vendor steps right */}
          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-4">
            {/* Client column */}
            <div className="flex flex-col gap-4">
              <div className="px-4 py-2 rounded-full self-start" style={{ background: 'rgba(112,245,154,0.07)', border: '1px solid rgba(112,245,154,0.15)' }}>
                <span className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: 'var(--lz-green)', letterSpacing: '0.12em' }}>{t.howItWorks.clientLabel}</span>
              </div>
              {t.howItWorks.steps.filter(s => s.role === 'client').map((step, i) => (
                <div key={i} className="p-6 rounded-2xl" style={{ background: 'var(--lz-card)', border: '1px solid var(--lz-border)' }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(112,245,154,0.2)')}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--lz-border)')}>
                  <div className="flex items-start gap-4">
                    <span className="text-[11px] font-mono font-semibold shrink-0 pt-0.5" style={{ color: 'rgba(112,245,154,0.5)' }}>{step.num}</span>
                    <div>
                      <h3 className="text-[15px] font-semibold mb-2" style={{ color: 'var(--lz-text-primary)', letterSpacing: '-0.02em' }}>{step.title}</h3>
                      <p className="text-[13px] leading-relaxed" style={{ color: 'var(--lz-text-secondary)' }}>{step.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Vendor column */}
            <div className="flex flex-col gap-4">
              <div className="px-4 py-2 rounded-full self-start" style={{ background: 'rgba(59,130,246,0.07)', border: '1px solid rgba(59,130,246,0.15)' }}>
                <span className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: '#60a5fa', letterSpacing: '0.12em' }}>{t.howItWorks.vendorLabel}</span>
              </div>
              {t.howItWorks.steps.filter(s => s.role === 'vendor').map((step, i) => (
                <div key={i} className="p-6 rounded-2xl" style={{ background: 'var(--lz-card)', border: '1px solid var(--lz-border)' }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(59,130,246,0.2)')}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--lz-border)')}>
                  <div className="flex items-start gap-4">
                    <span className="text-[11px] font-mono font-semibold shrink-0 pt-0.5" style={{ color: 'rgba(96,165,250,0.5)' }}>{step.num}</span>
                    <div>
                      <h3 className="text-[15px] font-semibold mb-2" style={{ color: 'var(--lz-text-primary)', letterSpacing: '-0.02em' }}>{step.title}</h3>
                      <p className="text-[13px] leading-relaxed" style={{ color: 'var(--lz-text-secondary)' }}>{step.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10">
            <Link href="/how-it-works" className="inline-flex items-center gap-2 text-[13px] transition-colors duration-200 hover:text-white" style={{ color: 'var(--lz-green)' }}>
              {locale === 'fr' ? 'Voir le fonctionnement complet' : locale === 'en' ? 'See full how it works' : 'Ver cómo funciona completo'}
              <Icon name="arrowRight" size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── FEATURES — asymmetric bento grid ─────────────────────────────── */}
      <section id="fonctionnalites" className="relative overflow-hidden" style={{ background: 'var(--lz-deep)', borderTop: '1px solid var(--lz-border)', borderBottom: '1px solid var(--lz-border)' }}>
        <div className="relative z-10 w-full" style={{ maxWidth: '1360px', margin: '0 auto', padding: 'clamp(80px, 8vw, 160px) clamp(24px, 4vw, 48px)' }}>
          {/* Header — right aligned on desktop */}
          <div className="mb-16 lg:text-right">
            <p className="lz-label mb-6">{t.features.eyebrow}</p>
            <h2 className="lz-h2 whitespace-pre-line mb-0" style={{ maxWidth: '480px', marginLeft: 'auto' }}>{t.features.title}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {t.features.cards.map((card, i) => (
              <Link
                key={i}
                href={card.href}
                className={`p-6 rounded-2xl block transition-all duration-200 group ${card.size === 'large' ? 'md:col-span-2 md:row-span-2' : card.size === 'wide' ? 'md:col-span-2' : ''}`}
                style={{ background: 'var(--lz-card)', border: '1px solid var(--lz-border)' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(112,245,154,0.25)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--lz-border)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div className={`flex flex-col ${card.size === 'large' ? 'h-full min-h-[220px]' : ''}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(112,245,154,0.08)', border: '1px solid rgba(112,245,154,0.15)', color: 'var(--lz-green)' }}>
                      <Icon name={card.icon} size={16} />
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200" style={{ color: 'var(--lz-green)' }}>
                      <Icon name="arrowRight" size={14} />
                    </div>
                  </div>
                  <h3 className="font-semibold mb-2" style={{ fontSize: card.size === 'large' ? '18px' : '15px', color: 'var(--lz-text-primary)', letterSpacing: '-0.02em', fontFamily: 'var(--font-dm-sans)' }}>{card.title}</h3>
                  <p className="leading-relaxed" style={{ fontSize: card.size === 'large' ? '14px' : '13px', color: 'var(--lz-text-secondary)', fontFamily: 'var(--font-inter)' }}>{card.desc}</p>

                  {card.size === 'large' && (
                    <div className="mt-auto pt-6">
                      <div className="rounded-xl overflow-hidden" style={{ background: 'var(--lz-deep)', border: '1px solid var(--lz-border)' }}>
                        <div className="flex items-center gap-1.5 px-3 py-2" style={{ background: 'var(--lz-raised)', borderBottom: '1px solid var(--lz-border)' }}>
                          <div className="w-2 h-2 rounded-full" style={{ background: '#ff5f57' }} />
                          <div className="w-2 h-2 rounded-full" style={{ background: '#febc2e' }} />
                          <div className="w-2 h-2 rounded-full" style={{ background: '#28c840' }} />
                          <span className="text-[9px] font-mono ml-2" style={{ color: '#3f3f46' }}>Caisse · Alice · PIN ✓</span>
                        </div>
                        <div className="p-3">
                          <div className="flex items-center gap-2 p-2 rounded-lg mb-2" style={{ background: 'rgba(112,245,154,0.05)', border: '1px solid rgba(112,245,154,0.12)' }}>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-semibold shrink-0" style={{ background: 'rgba(112,245,154,0.15)', color: 'var(--lz-green)' }}>M</div>
                            <div className="flex-1">
                              <div className="text-[10px] font-semibold" style={{ color: 'var(--lz-text-primary)' }}>Marie D.</div>
                              <div className="text-[8px] font-mono" style={{ color: 'var(--lz-green)' }}>200 pts · Récompense dispo</div>
                            </div>
                            <div className="text-[8px] font-mono px-1.5 py-0.5 rounded" style={{ background: 'rgba(112,245,154,0.1)', color: 'var(--lz-green)' }}>✓ ID</div>
                          </div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: '#52525b' }}>Montant</span>
                            <span className="text-[16px] font-semibold font-mono" style={{ color: 'var(--lz-text-primary)', letterSpacing: '-0.03em' }}>54,00 €</span>
                          </div>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: '#52525b' }}>Points gagnés</span>
                            <span className="text-[11px] font-mono font-semibold" style={{ color: 'var(--lz-green)' }}>+27 pts</span>
                          </div>
                          <div className="flex items-center gap-2 p-2 rounded-lg mb-3" style={{ background: 'rgba(112,245,154,0.06)', border: '1px solid rgba(112,245,154,0.15)' }}>
                            <span className="text-base">🎁</span>
                            <div>
                              <div className="text-[9px] font-semibold" style={{ color: 'var(--lz-text-primary)' }}>Récompense appliquée</div>
                              <div className="text-[8px] font-mono" style={{ color: 'var(--lz-green)' }}>−10 € · Vendeur a validé</div>
                            </div>
                          </div>
                          <div className="w-full py-2 rounded-lg text-center text-[11px] font-semibold" style={{ background: 'var(--lz-green)', color: '#050505' }}>Valider la vente</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {card.size === 'wide' && (
                    <div className="mt-4 flex items-center gap-3 flex-wrap">
                      {['🇫🇷 FR', '🇺🇸 EN', '🇲🇽 ES'].map((lang, li) => (
                        <span key={li} className="text-[11px] font-mono px-2.5 py-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--lz-border)', color: '#a1a1aa' }}>{lang}</span>
                      ))}
                      <span className="text-[11px]" style={{ color: '#52525b' }}>·</span>
                      {['EUR', 'USD', 'GBP', 'CHF', 'CAD'].map((cur, ci) => (
                        <span key={ci} className="text-[11px] font-mono px-2 py-1 rounded-lg" style={{ background: 'rgba(112,245,154,0.05)', border: '1px solid rgba(112,245,154,0.12)', color: 'var(--lz-green)' }}>{cur}</span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-10 text-right">
            <Link href="/features" className="inline-flex items-center gap-2 text-[13px] transition-colors duration-200 hover:text-white" style={{ color: 'var(--lz-green)' }}>
              {locale === 'fr' ? 'Toutes les fonctionnalités' : locale === 'en' ? 'All features' : 'Todas las funcionalidades'}
              <Icon name="arrowRight" size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── TERMINAL SECTION — asymmetric 60/40 image left / text right ──── */}
      <section className="relative overflow-hidden" style={{ background: 'var(--lz-bg)' }}>
        <div className="relative z-10 w-full" style={{ maxWidth: '1360px', margin: '0 auto', padding: 'clamp(80px, 8vw, 160px) clamp(24px, 4vw, 48px)' }}>
          <div className="flex flex-col lg:flex-row-reverse lg:items-center gap-12 lg:gap-20">
            {/* Right (visually left on desktop via row-reverse) — text (40%) */}
            <div className="lg:w-[40%]">
              <p className="lz-label mb-6">{locale === 'fr' ? 'Terminal client' : locale === 'en' ? 'Customer terminal' : 'Terminal cliente'}</p>
              <h2 className="lz-h2 whitespace-pre-line mb-6">
                {locale === 'fr' ? 'Le client consulte.\nLe vendeur valide.' : locale === 'en' ? 'Customer views.\nSeller validates.' : 'El cliente consulta.\nEl vendedor valida.'}
              </h2>
              <p className="lz-body mb-8">
                {locale === 'fr' ? 'Le terminal est placé face au client. Il saisit son numéro, voit ses points et récompenses disponibles. Il ne peut rien valider — c\'est le rôle du vendeur.'
                  : locale === 'en'? 'The terminal faces the customer. They enter their number, see their points and available rewards. They cannot validate anything — that\'s the seller\'s role.' :'El terminal está frente al cliente. Ingresa su número, ve sus puntos y recompensas disponibles. No puede validar nada — ese es el rol del vendedor.'}
              </p>
              <div className="space-y-3">
                {[
                  { icon: 'phone', text: locale === 'fr' ? 'Saisie du numéro uniquement' : locale === 'en' ? 'Number entry only' : 'Solo ingreso de número' },
                  { icon: 'reward', text: locale === 'fr' ? 'Consultation des points et récompenses' : locale === 'en' ? 'View points and rewards' : 'Consulta de puntos y recompensas' },
                  { icon: 'shield', text: locale === 'fr' ? 'Aucune validation possible côté client' : locale === 'en' ? 'No validation possible on client side' : 'Sin validación posible del lado del cliente' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(112,245,154,0.08)', border: '1px solid rgba(112,245,154,0.15)', color: 'var(--lz-green)' }}>
                      <Icon name={item.icon} size={14} />
                    </div>
                    <span className="text-[14px]" style={{ color: 'var(--lz-text-secondary)', fontFamily: 'var(--font-inter)' }}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Left (visually right on desktop) — terminal visual (60%) */}
            <div className="lg:w-[60%] flex justify-center">
              <TerminalScreen />
            </div>
          </div>
        </div>
      </section>

      {/* ── ONBOARDING — asymmetric 50/50 with staggered cards ──────────── */}
      <section className="relative overflow-hidden" style={{ background: 'var(--lz-deep)', borderTop: '1px solid var(--lz-border)', borderBottom: '1px solid var(--lz-border)' }}>
        <div className="relative z-10 w-full" style={{ maxWidth: '1360px', margin: '0 auto', padding: 'clamp(80px, 8vw, 160px) clamp(24px, 4vw, 48px)' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <p className="lz-label mb-6">{t.onboarding.eyebrow}</p>
              <h2 className="lz-h2 whitespace-pre-line mb-6">{t.onboarding.title}</h2>
              <p className="lz-body">
                {locale === 'fr' ? 'Configurez votre programme en quelques minutes. Aucune compétence technique requise.'
                  : locale === 'en'? 'Set up your program in minutes. No technical skills required.' :'Configura tu programa en minutos. Sin conocimientos técnicos.'}
              </p>
            </div>
            <div className="space-y-2">
              {t.onboarding.steps.map((step, i) => (
                <div key={i} className="flex items-center gap-4 p-4 rounded-xl transition-all duration-200 group cursor-default" style={{ border: '1px solid var(--lz-border)', background: 'var(--lz-card)' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(112,245,154,0.2)'; e.currentTarget.style.background = 'rgba(112,245,154,0.03)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--lz-border)'; e.currentTarget.style.background = 'var(--lz-card)'; }}>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(112,245,154,0.08)', border: '1px solid rgba(112,245,154,0.15)' }}>
                    <span className="text-[11px] font-mono font-semibold" style={{ color: 'var(--lz-green)' }}>{step.n}</span>
                  </div>
                  <span className="text-[14px] group-hover:text-white transition-colors" style={{ color: 'var(--lz-text-secondary)', fontFamily: 'var(--font-inter)' }}>{step.label}</span>
                  <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--lz-green)' }}>
                    <Icon name="arrowRight" size={14} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ──────────────────────────────────────────────────────── */}
      <section id="tarifs" className="relative overflow-hidden" style={{ background: 'var(--lz-bg)' }}>
        <div className="absolute pointer-events-none" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '700px', height: '500px', background: 'radial-gradient(ellipse, rgba(112,245,154,0.05) 0%, transparent 70%)', filter: 'blur(40px)' }} />
        <div className="relative z-10 w-full" style={{ maxWidth: '1360px', margin: '0 auto', padding: 'clamp(80px, 8vw, 160px) clamp(24px, 4vw, 48px)' }}>
          <div className="text-center mb-14">
            <p className="lz-label mb-6">{t.pricing.eyebrow}</p>
            <h2 className="lz-h2 whitespace-pre-line">{t.pricing.title}</h2>
          </div>

          <div className="flex items-center justify-center gap-3 mb-12">
            <span className="text-[13px]" style={{ color: pricingYearly ? 'var(--lz-text-secondary)' : 'var(--lz-text-primary)', fontFamily: 'var(--font-inter)' }}>{t.pricing.monthly}</span>
            <button onClick={() => setPricingYearly(!pricingYearly)} className="relative w-11 h-6 rounded-full transition-colors duration-200" style={{ background: pricingYearly ? 'var(--lz-green)' : 'rgba(255,255,255,0.1)' }}>
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform duration-200 ${pricingYearly ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
            <span className="text-[13px]" style={{ color: pricingYearly ? 'var(--lz-text-primary)' : 'var(--lz-text-secondary)', fontFamily: 'var(--font-inter)' }}>{t.pricing.yearly}</span>
            {pricingYearly && (
              <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(112,245,154,0.08)', border: '1px solid rgba(112,245,154,0.2)', color: 'var(--lz-green)' }}>{t.pricing.saving}</span>
            )}
          </div>

          <div className="max-w-md mx-auto">
            <div className="relative rounded-2xl p-8 overflow-hidden" style={{ background: 'var(--lz-card)', border: '1px solid rgba(112,245,154,0.2)', boxShadow: '0 0 0 1px rgba(112,245,154,0.06), 0 32px 80px rgba(0,0,0,0.8)' }}>
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(112,245,154,0.4), transparent)' }} />
              <div className="flex items-end gap-2 mb-2">
                <span className="font-light" style={{ fontSize: '52px', color: 'var(--lz-text-primary)', letterSpacing: '-0.04em', lineHeight: 1 }}>
                  {pricingYearly ? t.pricing.yearlyPrice : t.pricing.monthlyPrice}
                </span>
                <span className="text-[14px] mb-3" style={{ color: 'var(--lz-text-secondary)', fontFamily: 'var(--font-inter)' }}>
                  {pricingYearly ? t.pricing.yearlyPeriod : t.pricing.monthlyPeriod}
                </span>
              </div>
              <p className="text-[12px] mb-8" style={{ color: '#52525b', fontFamily: 'var(--font-inter)' }}>{pricingYearly ? t.pricing.yearlyNote : t.pricing.monthlyNote}</p>
              <ul className="space-y-3 mb-8">
                {t.pricing.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-[13px]" style={{ color: 'var(--lz-text-secondary)', fontFamily: 'var(--font-inter)' }}>
                    <span style={{ color: 'var(--lz-green)', flexShrink: 0 }}><Icon name="check" size={13} /></span>
                    {f}
                  </li>
                ))}
              </ul>
              <Link href="/signup" className="block w-full py-3.5 rounded-lg text-[14px] font-semibold text-center transition-all duration-200 hover:opacity-90" style={{ background: 'var(--lz-green)', color: '#050505', fontFamily: 'var(--font-dm-sans)' }}>
                {t.pricing.cta}
              </Link>
              <p className="text-center text-[11px] mt-3" style={{ color: '#52525b', fontFamily: 'var(--font-inter)' }}>{t.pricing.ctaNote}</p>
              <p className="text-center text-[11px] mt-1" style={{ color: 'rgba(112,245,154,0.5)', fontFamily: 'var(--font-mono-lz)' }}>{t.pricing.trialNote}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section id="faq" className="relative overflow-hidden" style={{ background: 'var(--lz-deep)', borderTop: '1px solid var(--lz-border)' }}>
        <div className="w-full" style={{ maxWidth: '1360px', margin: '0 auto', padding: 'clamp(80px, 8vw, 160px) clamp(24px, 4vw, 48px)' }}>
          {/* Asymmetric: label left, title right */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:gap-20 mb-16">
            <div className="lg:w-[35%]">
              <p className="lz-label mb-4">{t.faq.eyebrow}</p>
            </div>
            <div className="lg:w-[65%]">
              <h2 className="lz-h2">{t.faq.title}</h2>
            </div>
          </div>
          <div className="space-y-2" style={{ maxWidth: '680px', margin: '0 auto' }}>
            {t.faq.items.map((item, i) => (
              <div key={i} className="rounded-xl overflow-hidden transition-all duration-200" style={{ border: '1px solid var(--lz-border)', background: 'var(--lz-card)' }}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between px-5 py-4 text-left">
                  <span className="text-[14px] pr-4" style={{ color: '#d4d4d8', letterSpacing: '-0.01em', fontFamily: 'var(--font-inter)' }}>{item.q}</span>
                  <span className={`shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`} style={{ color: '#52525b' }}>
                    <Icon name="chevronDown" size={16} />
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5">
                    <p className="text-[13px] leading-relaxed" style={{ color: 'var(--lz-text-secondary)', fontFamily: 'var(--font-inter)' }}>{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden" style={{ background: 'var(--lz-bg)' }}>
        <div className="absolute pointer-events-none" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '800px', height: '500px', background: 'radial-gradient(ellipse, rgba(112,245,154,0.06) 0%, transparent 70%)', filter: 'blur(60px)' }} />
        <div className="relative z-10 w-full text-center" style={{ maxWidth: '1360px', margin: '0 auto', padding: 'clamp(100px, 10vw, 200px) clamp(24px, 4vw, 48px)' }}>
          <div className="flex justify-center mb-10">
            <LoyzenPicto size={52} />
          </div>
          {/* Playfair H1 for the final CTA — editorial moment */}
          <h2 className="lz-h1 whitespace-pre-line mb-7 mx-auto" style={{ maxWidth: '680px' }}>{t.finalCta.title}</h2>
          <p className="lz-body mb-12 mx-auto" style={{ fontSize: '17px', maxWidth: '400px' }}>{t.finalCta.subtitle}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link href="/signup" className="px-9 py-4 rounded-lg text-[15px] font-semibold transition-all duration-200 hover:opacity-90" style={{ background: 'var(--lz-green)', color: '#050505', boxShadow: '0 0 40px rgba(112,245,154,0.2)', fontFamily: 'var(--font-dm-sans)' }}>
              {t.finalCta.cta}
            </Link>
            <Link href="/login" className="px-9 py-4 rounded-lg text-[15px] transition-all duration-200 hover:text-white" style={{ color: 'var(--lz-text-secondary)', border: '1px solid var(--lz-border)', background: 'rgba(255,255,255,0.03)', fontFamily: 'var(--font-inter)' }}>
              {t.finalCta.secondary}
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter locale={locale} onLocaleChange={handleLocaleChange} />
    </div>
  );
}